<template>
  <button
    :class="[
      'action-button',
      `action-button--${variant}`,
      `action-button--${size}`,
      {
        'action-button--loading': loading,
        'action-button--disabled': disabled,
        'action-button--full-width': fullWidth,
      },
      customClass,
    ]"
    :disabled="disabled || loading"
    :type="type"
    v-bind="$attrs"
    @click="handleClick"
  >
    <div class="action-button__content">
      <!-- Loading Spinner -->
      <div v-if="loading" class="action-button__spinner">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          class="action-button__spinner-icon"
        >
          <circle
            class="action-button__spinner-circle"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="action-button__spinner-path"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>

      <!-- Icon (left) -->
      <div
        v-if="!loading && ($slots['icon-left'] || iconLeft)"
        class="action-button__icon action-button__icon--left"
      >
        <slot name="icon-left">
          <component v-if="iconLeft" :is="iconLeft" />
        </slot>
      </div>

      <!-- Content -->
      <span v-if="!loading" class="action-button__text">
        <slot />
      </span>
      <span v-else class="action-button__text">
        {{ loadingText }}
      </span>

      <!-- Icon (right) -->
      <div
        v-if="!loading && ($slots['icon-right'] || iconRight)"
        class="action-button__icon action-button__icon--right"
      >
        <slot name="icon-right">
          <component v-if="iconRight" :is="iconRight" />
        </slot>
      </div>
    </div>
  </button>
</template>

<script setup>
import { defineProps, defineEmits } from "vue";

const props = defineProps({
  variant: {
    type: String,
    default: "primary",
    validator: (value) =>
      [
        "primary",
        "secondary",
        "ghost",
        "danger",
        "tertiary",
        "outline",
        "text",
        "flat",
        "success",
      ].includes(value),
  },
  size: {
    type: String,
    default: "medium",
    validator: (value) => ["small", "medium", "large"].includes(value),
  },
  type: {
    type: String,
    default: "button",
    validator: (value) => ["button", "submit", "reset"].includes(value),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingText: {
    type: String,
    default: "Loading...",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  customClass: {
    type: String,
    default: "",
  },
  iconLeft: {
    type: [String, Object],
    default: null,
  },
  iconRight: {
    type: [String, Object],
    default: null,
  },
});

const emit = defineEmits(["click"]);

const handleClick = (event) => {
  if (!props.disabled && !props.loading) {
    emit("click", event);
  }
};
</script>

<style scoped>
/* Base Button Styles */
.action-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 12px;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  text-decoration: none;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
}

.action-button:focus-visible {
  outline: 2px solid var(--primary-blue);
  outline-offset: 2px;
}

.action-button:active {
  transform: scale(0.98);
}

.action-button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

.action-button--loading {
  cursor: not-allowed;
}

.action-button--full-width {
  width: 100%;
}

/* Button Content */
.action-button__content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
}

.action-button__text {
  font-weight: inherit;
  line-height: 1.5;
  letter-spacing: 0.024px;
  font-size: var(--fs-big);
  font-weight: 700;
}

/* Icons */
.action-button__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-button__icon svg {
  width: 20px;
  height: 20px;
}

/* Loading Spinner */
.action-button__spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-button__spinner-icon {
  width: 20px;
  height: 20px;
  animation: action-button-spin 1s linear infinite;
}

.action-button__spinner-circle {
  opacity: 0.25;
}

.action-button__spinner-path {
  opacity: 0.75;
}

@keyframes action-button-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Size Variants */
.action-button--small {
  padding: 8px 12px;
  font-size: var(--fs-small);
  line-height: 20px;
  min-height: 36px;
}

.action-button--small .action-button__icon svg {
  width: 16px;
  height: 16px;
}

.action-button--small .action-button__spinner-icon {
  width: 16px;
  height: 16px;
}

.action-button--medium {
  padding: 12px 16px;
  line-height: 24px;
  min-height: 48px;
}

.action-button--large {
  padding: 16px 24px;
  font-size: 18px;
  line-height: 28px;
  min-height: 56px;
}

.action-button--large .action-button__icon svg {
  width: 24px;
  height: 24px;
}

.action-button--large .action-button__spinner-icon {
  width: 24px;
  height: 24px;
}

/* Primary Variant */
.action-button--primary {
  background-color: var(--primary-blue);
  color: white;
  border: 1px solid var(--primary-blue);
}

.action-button--primary:hover:not(.action-button--disabled):not(
    .action-button--loading
  ) {
  background-color: #002a4a;
  border-color: #002a4a;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 61, 106, 0.3);
}

.action-button--primary:active:not(.action-button--disabled) {
  background-color: #001f3a;
  border-color: #001f3a;
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 61, 106, 0.2);
}

/* Secondary Variant */
.action-button--secondary {
  background-color: white;
  color: var(--primary-blue);
  border: 1px solid var(--primary-blue);
}

.action-button--secondary:hover:not(.action-button--disabled):not(
    .action-button--loading
  ) {
  background-color: #f2f3fb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 61, 106, 0.15);
}

.action-button--secondary:active:not(.action-button--disabled) {
  background-color: #e6e7f0;
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 61, 106, 0.1);
}

/* Ghost Variant (transparent/no background) */
.action-button--ghost {
  background-color: transparent;
  color: var(--primary-blue);
  border: 1px solid transparent;
}

.action-button--ghost:hover:not(.action-button--disabled):not(
    .action-button--loading
  ) {
  background-color: #f9fafb;
  color: #002a4a;
  transform: translateY(-1px);
}

.action-button--ghost:active:not(.action-button--disabled) {
  background-color: #f2f3fb;
  color: #001f3a;
  transform: translateY(0);
}

/* Danger Variant (red for delete actions) */
.action-button--danger {
  background-color: var(--error-red);
  color: white;
  border: 1px solid var(--error-red);
}

.action-button--danger:hover:not(.action-button--disabled):not(
    .action-button--loading
  ) {
  background-color: #a01818;
  border-color: #a01818;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(194, 29, 29, 0.3);
}

.action-button--danger:active:not(.action-button--disabled) {
  background-color: #8b1515;
  border-color: #8b1515;
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(194, 29, 29, 0.2);
}

/* Tertiary Variant (light blue background) */
.action-button--tertiary {
  background-color: #f2f3fb;
  color: var(--primary-blue);
  border: 1px solid #f2f3fb;
}

.action-button--tertiary:hover:not(.action-button--disabled):not(
    .action-button--loading
  ) {
  background-color: #e6e7f0;
  border-color: #e6e7f0;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 61, 106, 0.1);
}

.action-button--tertiary:active:not(.action-button--disabled) {
  background-color: #d9dbe8;
  border-color: #d9dbe8;
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 61, 106, 0.08);
}

/* Outline Variant (border only) */
.action-button--outline {
  background-color: transparent;
  color: var(--primary-blue);
  border: 1px solid var(--primary-blue);
}

.action-button--outline:hover:not(.action-button--disabled):not(
    .action-button--loading
  ) {
  background-color: var(--primary-blue);
  color: white;
  border-color: var(--primary-blue);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 61, 106, 0.2);
}

.action-button--outline:active:not(.action-button--disabled) {
  background-color: #002a4a;
  color: white;
  border-color: #002a4a;
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 61, 106, 0.15);
}

/* Text Variant (minimal, text only) */
.action-button--text {
  background-color: transparent;
  color: var(--primary-blue);
  border: 1px solid transparent;
  padding-left: 8px;
  padding-right: 8px;
}

.action-button--text:hover:not(.action-button--disabled):not(
    .action-button--loading
  ) {
  background-color: rgba(0, 61, 106, 0.05);
  color: #002a4a;
  transform: translateY(-1px);
}

.action-button--text:active:not(.action-button--disabled) {
  background-color: rgba(0, 61, 106, 0.1);
  color: #001f3a;
  transform: translateY(0);
}

/* Flat Variant (minimal background, no border) */
.action-button--flat {
  background-color: #f9fafb;
  color: var(--primary-blue);
  border: 1px solid transparent;
}

.action-button--flat:hover:not(.action-button--disabled):not(
    .action-button--loading
  ) {
  background-color: #f2f3fb;
  color: #002a4a;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 61, 106, 0.08);
}

.action-button--flat:active:not(.action-button--disabled) {
  background-color: #e6e7f0;
  color: #001f3a;
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(0, 61, 106, 0.06);
}

/* Success Variant (Eddy Green) */
.action-button--success {
  background-color: var(--primary-green);
  color: var(--white);
  border: 1px solid var(--primary-green);
}

.action-button--success:hover:not(.action-button--disabled):not(
    .action-button--loading
  ) {
  background-color: #007a4f;
  border-color: #007a4f;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 146, 94, 0.3);
}

.action-button--success:active:not(.action-button--disabled) {
  background-color: #006742;
  border-color: #006742;
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 146, 94, 0.2);
}

/* Disabled States */
.action-button--disabled.action-button--primary {
  background-color: var(--neutral-100);
  border-color: var(--neutral-100);
  color: var(--neutral-700);
}

.action-button--disabled.action-button--secondary {
  background-color: #f5f5f5;
  border-color: #d1d5db;
  color: var(--neutral-900);
}

.action-button--disabled.action-button--ghost {
  background-color: transparent;
  color: var(--neutral-700);
}

.action-button--disabled.action-button--danger {
  background-color: #9ca3af;
  border-color: #9ca3af;
  color: white;
}

.action-button--disabled.action-button--tertiary {
  background-color: #e5e7eb;
  border-color: #e5e7eb;
  color: var(--neutral-900);
}

.action-button--disabled.action-button--outline {
  background-color: transparent;
  border-color: #d1d5db;
  color: var(--neutral-700);
}

.action-button--disabled.action-button--text {
  background-color: transparent;
  color: var(--neutral-700);
}

.action-button--disabled.action-button--flat {
  background-color: #f5f5f5;
  color: var(--neutral-900);
}

.action-button--disabled.action-button--success {
  background-color: var(--neutral-100);
  border-color: var(--neutral-100);
  color: var(--neutral-700);
}

/* Responsive Design */
@media (max-width: 768px) {
  .action-button--medium {
    padding: 10px 14px;
    min-height: 44px;
  }

  .action-button--large {
    padding: 14px 20px;
    font-size: var(--fs-medium);
    min-height: 52px;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .action-button {
    transition: none;
  }

  .action-button:hover:not(.action-button--disabled):not(
      .action-button--loading
    ) {
    transform: none;
  }

  .action-button:active:not(.action-button--disabled) {
    transform: none;
  }

  .action-button__spinner-icon {
    animation: none;
  }
}

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .action-button--primary {
    border-width: 2px;
  }

  .action-button--secondary {
    border-width: 2px;
  }

  .action-button--ghost {
    border: 1px solid currentColor;
  }
}
</style>
